import React, { createContext, useContext, useEffect, useMemo } from "react";
import type { EnrolledProgram, ModuleDataDto } from "../lib/setupApi";
import { defaultModuleDataDto } from "../lib/setupApi";
import { mergeSessionPatch, type AppSession } from "@repo/utils";
import {
  useSelectedProgram,
  type SelectableProgram,
} from "../hooks/useSelectedProgram";

type ModuleDataContextValue = ModuleDataDto & {
  selectedProgram: EnrolledProgram | null;
  setSelectedProgram: (program: SelectableProgram | null) => void;
  handleLogOut?: () => void;
};

const ModuleDataContext = createContext<ModuleDataContextValue>({
  ...defaultModuleDataDto,
  selectedProgram: null,
  setSelectedProgram: () => {},
  handleLogOut: undefined,
});

type ModuleDataProviderProps = {
  value: ModuleDataDto;
  handleLogOut?: () => void;
  children: React.ReactNode;
};

function toNumberOrNull(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function buildSessionPatchFromProgram(
  selected: EnrolledProgram,
  normalizedPrograms: EnrolledProgram[] | null,
): Partial<AppSession> {
  const selectedId = selected.program_id ?? null;
  const updatedEnrolled = Array.isArray(normalizedPrograms)
    ? normalizedPrograms.map((p) => {
        if (p.program_id === selectedId) {
          return { ...p, ...selected, isDefault: true };
        }
        return { ...p, isDefault: false };
      })
    : null;

  return {
    programId: selectedId,
    examId: toNumberOrNull(selected.examId ?? selected.mdsExamId),
    examName: selected.examName ?? selected.mdsExamName ?? null,
    mdsExamId: toNumberOrNull(selected.mdsExamId),
    mdsExamName: selected.mdsExamName ?? null,
    gradeId: toNumberOrNull(selected.mdsGradeId ?? selected.gradeId),
    mdsGradeId: toNumberOrNull(selected.mdsGradeId),
    tenantId: selected.tenant_id ?? null,
    subtenantId:
      selected.subtenant_id != null &&
      Number.isFinite(Number(selected.subtenant_id))
        ? Number(selected.subtenant_id)
        : null,
    verticalId: toNumberOrNull(selected.vertical_id),
    enrolledPrograms: updatedEnrolled,
  };
}

export const ModuleDataProvider = ({
  value,
  handleLogOut,
  children,
}: ModuleDataProviderProps) => {
  const { normalizedPrograms, selectedProgram, setSelectedProgram } =
    useSelectedProgram(value.enrolledPrograms);

  useEffect(() => {
    if (!selectedProgram?.program_id) return;
    mergeSessionPatch(
      buildSessionPatchFromProgram(selectedProgram, normalizedPrograms),
    );
  }, [selectedProgram, normalizedPrograms]);

  const contextValue = useMemo(
    () => ({
      ...value,
      enrolledPrograms: normalizedPrograms,
      selectedProgram,
      setSelectedProgram,
      handleLogOut,
    }),
    [
      value,
      normalizedPrograms,
      selectedProgram,
      setSelectedProgram,
      handleLogOut,
    ],
  );

  return (
    <ModuleDataContext.Provider value={contextValue}>
      {children}
    </ModuleDataContext.Provider>
  );
};

export const useModuleData = () => useContext(ModuleDataContext);
